// eslint-disable-next-line no-unused-vars
import React from 'react'; // Required for JSX in error toast content
import axios from 'axios';
import { toast } from 'react-toastify';
import { getAccessToken, setAccessToken, clearAccessToken } from './token';
import { API_BASE, CURRENT_TENANT, isDevelopment } from './env';

// Resolve API base URL
// - If VITE_API_BASE is set, use it (origin only; no /api)
// - Otherwise use same-origin (blank) and let the dev proxy handle /api
const resolvedBase = API_BASE;

// Tour sandbox mode - when active, write operations are simulated
let tourSandboxMode = false;

export const setTourSandboxMode = (active) => {
  tourSandboxMode = active;
};

export const isTourSandboxMode = () => tourSandboxMode;

/**
 * Read a cookie value by name (for CSRF double-submit tokens).
 * Flask-JWT-Extended sets non-HttpOnly CSRF cookies alongside JWT cookies.
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

const api = axios.create({
  baseURL: resolvedBase,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use(config => {
  // Prefix /api when hitting relative paths so baseURL stays as origin
  if (config.url && typeof config.url === 'string') {
    if (!config.url.startsWith('http')) {
      if (config.url.startsWith('/')) {
        if (!config.url.startsWith('/api')) {
          config.url = '/api' + config.url;
        }
      } else if (!config.url.startsWith('api/')) {
        config.url = '/api/' + config.url;
      } else {
        config.url = '/' + config.url;
      }
    }
  }

  config.headers = config.headers || {};

  // Tour sandbox mode - intercept write operations and simulate success
  if (tourSandboxMode) {
    const method = (config.method || 'get').toLowerCase();
    const isWriteOperation = ['post', 'put', 'patch', 'delete'].includes(method);
    const url = config.url || '';

    // Allow certain endpoints even in sandbox mode (auth, settings, analytics)
    const allowedInSandbox = [
      '/users/login',
      '/users/logout',
      '/users/token',
      '/users/me/settings',
      '/onboarding',
      '/analytics',
      '/telemetry',
    ];
    const isAllowed = allowedInSandbox.some(ep => url.includes(ep));

    if (isWriteOperation && !isAllowed) {
      // Store original adapter and replace with sandbox simulation
      config.adapter = () => {
        // Show toast notification about sandbox mode
        toast.info(
          <div>
            <div className="font-medium">Sandbox Mode</div>
            <div className="text-sm opacity-90">This action was simulated - no data was saved.</div>
          </div>,
          { autoClose: 3000, icon: '🧪' }
        );

        // Return a simulated successful response
        return Promise.resolve({
          data: {
            success: true,
            message: 'Simulated in tour sandbox mode',
            id: Date.now(), // Fake ID for created resources
          },
          status: 200,
          statusText: 'OK (Sandbox)',
          headers: {},
          config: config,
        });
      };
    }
  }

  // In development mode, add X-Tenant-ID header for multi-tenant testing
  // This allows testing different tenants on localhost
  if (isDevelopment && CURRENT_TENANT) {
    config.headers['X-Tenant-ID'] = CURRENT_TENANT;
  }

  const token = getAccessToken();
  // Do NOT attach Authorization to the refresh endpoint; it must rely on the refresh cookie
  const isRefresh = typeof config.url === 'string' && config.url.includes('/users/token/refresh');
  if (token && !isRefresh) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    delete config.headers['Authorization'];
    // When authenticating via cookie (no Authorization header), attach the CSRF
    // double-submit token.  Flask-JWT-Extended requires this for state-changing
    // methods (POST/PUT/PATCH/DELETE) when JWT_COOKIE_CSRF_PROTECT is enabled.
    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const csrfToken = isRefresh
        ? getCookie('csrf_refresh_token')
        : getCookie('csrf_access_token');
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }
    }
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      const url = (originalRequest && typeof originalRequest.url === 'string') ? originalRequest.url : '';
      const isRefresh = url.includes('/users/token/refresh');
      const isLogin = url.includes('/users/login');

      // Never attempt refresh when the 401 came from the refresh endpoint itself
      if (isRefresh) {
        clearAccessToken();
        toast.error('Session expired. Please log in again.');
        if (window && window.location && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      // Do not try to refresh on a failed login — just surface the error
      if (isLogin) {
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          // Attempt to get a new access token using the refresh token cookie
          const refreshResponse = await api.post(
            '/users/token/refresh',
            null,
            { withCredentials: true }
          );
          const newToken = refreshResponse.data.access_token;
          // Store and set the new token
          setAccessToken(newToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          // Retry original request
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed: remove token and reject
          clearAccessToken();
          toast.error('Session expired. Please log in again.');
          if (window && window.location && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
    }
    // For non-401 errors, surface server message via enhanced toast notifications
    try {
      const resp = error.response;
      const url = originalRequest?.url || '';

      // Skip toast for non-critical endpoints (analytics, telemetry, etc.)
      const silentEndpoints = ['/onboarding-analytics', '/telemetry', '/metrics'];
      const isSilentEndpoint = silentEndpoints.some(ep => url.includes(ep));

      if (resp && resp.status !== 401 && !isSilentEndpoint) {
        const data = resp.data || {};

        // Extract request ID for support reference
        const requestId = resp.headers?.['x-request-id'] || resp.headers?.['X-Request-ID'];

        // Extract error message with fallback chain
        let msg = data.msg || data.message || data.detail || data.error;

        // Handle validation errors (array of errors)
        if (data.errors && Array.isArray(data.errors)) {
          msg = data.errors.join(', ');
        } else if (data.errors && typeof data.errors === 'object') {
          // Handle object-based validation errors
          msg = Object.values(data.errors).flat().join(', ');
        }

        // Fallback to generic message
        if (!msg) {
          msg = error.message || `Request failed (${resp.status})`;
        }

        // Append request ID to server errors for support tickets
        let toastContent = msg;
        if (resp.status >= 500 && requestId) {
          toastContent = (
            <div>
              <div>{msg}</div>
              <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8 }}>
                Request ID: <code style={{ userSelect: 'all', cursor: 'pointer' }}>{requestId}</code>
              </div>
            </div>
          );
        }

        // Show appropriate notification based on status code
        if (resp.status >= 500) {
          toast.error(toastContent, { autoClose: 10000 });
        } else if (resp.status === 403) {
          toast.error(`Access denied: ${msg}`);
        } else if (resp.status === 404) {
          toast.error(`Not found: ${msg}`);
        } else if (resp.status === 400) {
          toast.error(`${msg}`);
        } else {
          toast.error(String(msg));
        }
      }
    } catch {
      // ignore toast failure
    }
    return Promise.reject(error);
  }
);

export default api;
